import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { RootMenuItem as RootMenuItemType } from '../../types';
import { FeatureFlag } from 'app/api/services/site-configs/types';
import { MENU_ITEM_TYPE } from 'app/modules/Navigation/types';

import { Hint } from 'app/design-system/components/Popper';
import { useTranslation } from 'app/lang';
import { useFeatureFlag } from 'app/lib/feature-flags';
import { getDictionaryDataItems, parseLocationPath } from 'app/modules/Navigation/utils';
import { fetchPrematchContent } from 'app/modules/Prematch/actions';
import { subscribeToFavorites } from 'app/modules/PrematchFavorites/actions';

import { createItemTypeClassGetter } from '../../styles/menu-item-type-styles';
import { createMenuItemTestDataAttributesGetter } from '../../test-data-helpers';
import IconCell from './IconCell';

import styles from '../../styles/LeftMenu.module.scss';

const SIGNATURE = 'wholePrematch';

const getItemTypeClass = createItemTypeClassGetter(
  MENU_ITEM_TYPE.SPORT,
  styles.sportItem,
);

const getMenuItemTestDataAttributes = createMenuItemTestDataAttributesGetter(
  'root-menu-item',
  MENU_ITEM_TYPE.SPORT,
);

type RootMenuItemProps = {
  entry: RootMenuItemType;
  showHint: boolean;
};

const RootMenuItem: React.FC<RootMenuItemProps> = ({ entry, showHint }) => {
  const dispatch = useDispatch();
  const isPrematchWS = useFeatureFlag(FeatureFlag.PREMATCH_WS);
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const handleClick = useCallback(() => {
    if (entry.location !== pathname) {
      return;
    }

    switch (entry.key) {
      case 'favorites':
        return dispatch(subscribeToFavorites());
      default: {
        const {
          sportId,
          tournamentUnionId,
          tournamentId,
          tournamentStageId,
        } = parseLocationPath(pathname);

        const payload = {
          sportId,
          tournamentUnionId,
          tournamentId,
          tournamentStageId,
          signature: SIGNATURE,
        };
        return dispatch(fetchPrematchContent(payload));
      }
    }
  }, [pathname, entry.location, entry.key]);

  const className: string = classNames(
    getItemTypeClass(entry.type),
    { [styles.active]: entry.active },
  );

  return (
    <li className={className}>
      <Link to={entry.location}
            className={styles.rootItemLink}
            onClick={isPrematchWS ? undefined : handleClick}
            {...entry.dataGaValues}
            title={showHint ? undefined : getDictionaryDataItems(t, entry.title)}
            {...getMenuItemTestDataAttributes(entry)}>
        {showHint ? (
          <Hint childrenWrapperTag="div"
                content={getDictionaryDataItems(t, entry.label)}>
            <IconCell entry={entry} />
          </Hint>
        ) : (
          <IconCell entry={entry} />
        )}

        {!entry.caption
          ? (
            <div className={styles.label}
                 data-test="item-label">
              {getDictionaryDataItems(t, entry.label)}
            </div>
          )
          : (
            <div className={styles.labelAndCaptionContainer}>
              <div className={styles.label}
                   data-test="item-label">
                {getDictionaryDataItems(t, entry.label)}
              </div>
              <div className={styles.caption}
                   data-test="item-caption">
                {entry.caption}
              </div>
            </div>
          )
        }
      </Link>
    </li>
  );
};

export default RootMenuItem;
